import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PasswordRecoveryService } from '../../services/password-recovery.service';
import { CustomFooterComponent } from '../custom-footer/custom-footer.component';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

declare global {
  interface Window {
    grecaptcha?: {
      ready(callback: () => void): void;
      execute(siteKey: string, options: { action: string }): Promise<string>;
      enterprise?: {
        ready(callback: () => void): void;
        execute(siteKey: string, options: { action: string }): Promise<string>;
      };
    };
  }
}

@Component({
  selector: 'app-password-recovery',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomFooterComponent, TranslateModule],
  templateUrl: './password-recovery.component.html',
  styleUrls: ['./password-recovery.component.scss']
})
export class PasswordRecoveryComponent implements OnInit {
  email = '';
  isLoading = false;
  message = '';
  messageType: 'success' | 'error' | '' = '';

  private readonly recaptchaSiteKey = environment.recaptchaSiteKey || '';
  private recaptchaLoader: Promise<void> | null = null;

  constructor(
    private readonly passwordRecoveryService: PasswordRecoveryService,
    private readonly router: Router,
    private readonly translate: TranslateService
  ) {
    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    const navEmail = typeof navigationState?.['email'] === 'string' ? navigationState['email'] : '';
    const historyEmail =
      typeof globalThis?.history?.state?.email === 'string' ? globalThis.history.state.email : '';
    this.email = navEmail || historyEmail || '';
  }

  async ngOnInit(): Promise<void> {
    if (!this.recaptchaSiteKey) {
      return;
    }
    try {
      await this.loadRecaptchaScript();
    } catch (error) {
      this.setMessage('error', this.translate.instant('passwordRecovery.errors.unavailable'));
    }
  }

  async submit(event?: Event): Promise<void> {
    event?.preventDefault();
    if (this.isLoading) {
      return;
    }

    const normalizedEmail = (this.email || '').trim();
    if (!normalizedEmail) {
      this.setMessage('error', this.translate.instant('passwordRecovery.messages.emailRequired'));
      return;
    }

    if (!this.recaptchaSiteKey) {
      this.setMessage('error', this.translate.instant('passwordRecovery.messages.recaptchaMissing'));
      return;
    }

    this.isLoading = true;
    this.setMessage('', '');

    try {
      const recaptchaToken = await this.executeRecaptcha('password_recovery');
      const result = await firstValueFrom(
        this.passwordRecoveryService.requestPasswordRecovery(normalizedEmail, recaptchaToken)
      );

      if (result === true) {
        this.setMessage('success', this.translate.instant('passwordRecovery.messages.success'));
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2500);
      } else {
        this.setMessage('error', this.translate.instant('passwordRecovery.messages.notFound'));
      }
    } catch (error: unknown) {
      const message = error instanceof Error
        ? error.message
        : this.translate.instant('passwordRecovery.messages.genericError');
      this.setMessage('error', message);
    } finally {
      this.isLoading = false;
    }
  }

  goBack(event?: Event): void {
    event?.preventDefault();
    this.router.navigate(['/login'], {
      state: { email: this.email }
    });
  }

  private setMessage(type: 'success' | 'error' | '', message: string): void {
    this.messageType = type;
    this.message = message;
  }

  private async loadRecaptchaScript(): Promise<void> {
    if (typeof window === 'undefined') {
      throw new Error(this.translate.instant('passwordRecovery.errors.unavailableEnv'));
    }

    if (window.grecaptcha?.enterprise || window.grecaptcha) {
      return;
    }

    if (this.recaptchaLoader) {
      return this.recaptchaLoader;
    }

    this.recaptchaLoader = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-recaptcha-enterprise="true"]');
      if (existingScript) {
        if (existingScript.dataset['loaded'] === 'true') {
          resolve();
          return;
        }
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener(
          'error',
          () => {
            this.recaptchaLoader = null;
            reject(new Error(this.translate.instant('passwordRecovery.errors.unavailable')));
          },
          { once: true }
        );
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.recaptchaSiteKey}`;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-recaptcha-enterprise', 'true');
      script.onload = () => {
        script.dataset['loaded'] = 'true';
        resolve();
      };
      script.onerror = () => {
        this.recaptchaLoader = null;
        reject(new Error(this.translate.instant('passwordRecovery.errors.unavailable')));
      };
      document.head.appendChild(script);
    });

    return this.recaptchaLoader;
  }

  private async executeRecaptcha(action: string): Promise<string> {
    await this.loadRecaptchaScript();

    const provider = window.grecaptcha?.enterprise ?? window.grecaptcha;
    if (!provider || typeof provider.ready !== 'function' || typeof provider.execute !== 'function') {
      throw new Error(this.translate.instant('passwordRecovery.errors.unavailable'));
    }

    return new Promise<string>((resolve, reject) => {
      provider.ready(() => {
        provider.execute(this.recaptchaSiteKey, { action })
          .then((token: string) => {
            if (!token) {
              reject(new Error(this.translate.instant('passwordRecovery.errors.token')));
              return;
            }
            resolve(token);
          })
          .catch(() => reject(new Error(this.translate.instant('passwordRecovery.errors.token'))));
      });
    });
  }
}
